import { ChevronLeft, ChevronRight, ImageOff, ImagePlus, Loader, Star, X } from 'lucide-react'
import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react'

import { Button, PhotoSourceSheet, PhotoViewer, ProgressRing, formatBytes } from '@krp/ui-kit'

import '../../../shared/ui/onec.css'
import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import { useUnsavedGuard } from '../../../shared/ui/useUnsavedGuard'
import type { PhotoUploadViewVm, ProductPhotosActions, ProductPhotosVm } from '../model/types'

/** Насколько надо сдвинуть палец по горизонтали, чтобы это считалось «листанием», а не нажатием. */
const SWIPE_DISTANCE = 48

/** Идущая загрузка: превью снимка, «часы» по реальным байтам, слова этапа и «Отменить». */
function UploadRun({ view, onCancel }: { view: PhotoUploadViewVm; onCancel: () => void }) {
  const percent = view.fraction === undefined ? null : Math.round(view.fraction * 100)
  const style = { '--photo-progress': `${view.fraction ?? 0}turn` } as CSSProperties
  return (
    <div className="onec-photo-run" role="status" aria-live="polite" style={style} data-phase={view.phase}>
      <div className="onec-photo-run__shot">
        {view.previewUrl === null ? <ImagePlus size={40} aria-hidden="true" /> : <img src={view.previewUrl} alt={`Снимок ${view.fileName}`} />}
        {view.previewUrl !== null && view.fraction !== undefined && <span className="onec-photo-run__veil" aria-hidden="true" />}
        <span className="onec-photo-run__ring">
          <ProgressRing label={view.stage} value={view.fraction} size={72} valueText={view.stage}>
            {percent === null ? (
              <Loader size={22} aria-hidden="true" />
            ) : (
              <span className="onec-photo-run__percent">{percent}%</span>
            )}
          </ProgressRing>
        </span>
      </div>
      <p className="onec-photo-run__caption">{view.stage}</p>
      {view.phase === 'sending' && view.total !== null && (
        <p className="onec-photo-run__meta">
          {formatBytes(view.loaded)} из {formatBytes(view.total)}
        </p>
      )}
      {view.cancelable && (
        <div className="onec-command__actions">
          <Button variant="ghost" icon={<X size={16} aria-hidden="true" />} onClick={onCancel}>
            Отменить
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Фото товара одной базы 1С прямо в карточке: слайдер кадров, главное фото и добавление нового снимка.
 *
 * - Фото нет — на их месте большая плитка «Добавить фото»; есть — рядом с кадром ещё одна плитка «Добавить». Плитка открывает лист
 *   «Сфотографировать / Выбрать из галереи или файлов» (RP-FB57, общий `PhotoSourceSheet` из ui-kit).
 * - Листать можно свайпом, стрелками (и клавишами ← →) и точками под кадром; номер кадра — словами «2 из 5».
 *   Жест не единственный способ: у каждого свайпа есть кнопка.
 * - Кадр не загрузился — значок и слова вместо картинки, кнопка «Повторить».
 * - Главный кадр отмечен значком и словом. Нажатие на кадр открывает его на весь экран.
 */
export function ProductPhotoGallery({ vm, actions }: { vm: ProductPhotosVm; actions: ProductPhotosActions }) {
  const swipeFrom = useRef<number | null>(null)
  const swiped = useRef(false)
  const [viewing, setViewing] = useState(false)
  const [choosing, setChoosing] = useState(false)
  const [broken, setBroken] = useState<readonly string[]>([])
  const [attempt, setAttempt] = useState(0)
  const frames = vm.frames
  const current = vm.current
  const at = Math.max(
    0,
    frames.findIndex((frame) => frame.selected),
  )
  const run = vm.uploadView
  const canWrite = vm.configurationVersion !== null && vm.configurationVersion !== ''
  const addDisabled = !canWrite || vm.upload.busy
  // Команда фото без итога: перезагрузка потеряла бы ключ для проверки исхода.
  useUnsavedGuard(vm.upload.busy || vm.mainBusy)

  const go = (step: number) => {
    const target = frames[at + step]
    if (target !== undefined) actions.select(target.fileKey)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      go(-1)
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      go(1)
    }
  }
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    swipeFrom.current = event.clientX
    swiped.current = false
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const from = swipeFrom.current
    swipeFrom.current = null
    if (from === null) return
    const shift = event.clientX - from
    if (Math.abs(shift) < SWIPE_DISTANCE) return
    swiped.current = true
    go(shift < 0 ? 1 : -1)
  }

  const addTile = (label: string, empty: boolean) => (
    <button
      type="button"
      className="onec-gallery__add"
      data-empty={empty ? 'true' : undefined}
      disabled={addDisabled}
      onClick={() => setChoosing(true)}
    >
      <ImagePlus size={empty ? 36 : 28} aria-hidden="true" />
      <span>{label}</span>
    </button>
  )

  const isBroken = current !== null && broken.includes(current.url)
  const showEmpty = vm.query.status === 'empty'
  return (
    <section className="onec-gallery" aria-label="Фото товара">
      <PhotoSourceSheet open={choosing} onClose={() => setChoosing(false)} onFile={actions.upload} />
      {!showEmpty && <OneCQueryState query={vm.query} emptyText="" onRetry={actions.reload} />}

      {showEmpty && <div className="onec-gallery__row">{addTile('Добавить фото', true)}</div>}

      {current !== null && (
        <div className="onec-gallery__row">
          <figure className="onec-gallery__figure" aria-roledescription="карусель" aria-label="Кадры товара">
            <div
              className="onec-gallery__stage"
              tabIndex={0}
              onKeyDown={onKeyDown}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onPointerCancel={() => {
                swipeFrom.current = null
              }}
            >
              {isBroken ? (
                <div className="onec-gallery__broken" role="alert">
                  <ImageOff size={32} aria-hidden="true" />
                  <p>Фото не загрузилось</p>
                  <Button
                    onClick={() => {
                      setBroken((list) => list.filter((url) => url !== current.url))
                      setAttempt((value) => value + 1)
                    }}
                  >
                    Повторить
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="onec-gallery__open"
                  aria-label="Открыть фото на весь экран"
                  aria-haspopup="dialog"
                  onClick={() => {
                    if (swiped.current) {
                      swiped.current = false
                      return
                    }
                    setViewing(true)
                  }}
                >
                  <img
                    key={`${current.url}#${String(attempt)}`}
                    src={current.url}
                    alt={current.isMain ? 'Главное фото товара' : 'Фото товара'}
                    draggable={false}
                    onError={() => setBroken((list) => (list.includes(current.url) ? list : [...list, current.url]))}
                  />
                </button>
              )}
              {frames.length > 1 && (
                <>
                  <button
                    type="button"
                    className="onec-gallery__arrow"
                    data-side="prev"
                    aria-label="Предыдущее фото"
                    disabled={at === 0}
                    onClick={() => go(-1)}
                  >
                    <ChevronLeft size={22} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="onec-gallery__arrow"
                    data-side="next"
                    aria-label="Следующее фото"
                    disabled={at === frames.length - 1}
                    onClick={() => go(1)}
                  >
                    <ChevronRight size={22} aria-hidden="true" />
                  </button>
                </>
              )}
              <span className="onec-gallery__count" role="status">
                {at + 1} из {frames.length}
              </span>
            </div>

            {frames.length > 1 && (
              <ul className="onec-gallery__dots" aria-label="Выбор кадра">
                {frames.map((frame, index) => (
                  <li key={frame.fileKey}>
                    <button
                      type="button"
                      aria-label={`Фото ${String(index + 1)}${frame.isMain ? ', главное' : ''}`}
                      aria-current={frame.selected ? 'true' : undefined}
                      onClick={() => actions.select(frame.fileKey)}
                    >
                      <span className="onec-gallery__dot" data-main={frame.isMain ? 'true' : undefined} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <figcaption className="onec-gallery__caption">
              {current.isMain ? (
                <span className="onec-gallery__main">
                  <Star size={14} aria-hidden="true" /> Главное фото
                </span>
              ) : (
                <Button disabled={!canWrite || vm.mainBusy} onClick={() => actions.setMain(current.fileKey)}>
                  Сделать главным
                </Button>
              )}
            </figcaption>
          </figure>
          {addTile('Добавить', false)}
        </div>
      )}

      {!canWrite && (showEmpty || current !== null) && (
        <p className="onec-field__note">Добавить фото можно, когда база ответит на чтение.</p>
      )}

      {run !== null && <UploadRun view={run} onCancel={() => actions.cancel(run.key)} />}

      {vm.commands.map((command) => (
        <CommandStatus
          key={command.key}
          command={command}
          onResend={() => actions.resend(command.key)}
          onDismiss={() => actions.dismiss(command.key)}
        />
      ))}

      {viewing && current !== null && (
        <PhotoViewer
          src={current.url}
          alt={current.isMain ? 'Главное фото товара' : 'Фото товара'}
          onClose={() => setViewing(false)}
        />
      )}
    </section>
  )
}
